/**
 * Hit-area helpers for parent-image size changes.
 * Kept separate so canvas / panel / timeline resize paths stay unchanged
 * aside from a thin call into this module.
 */
import {
  rebuildHitAreaAnimationEntries,
  syncHitAreasToParentImageSize,
  toNumber,
  wouldHitAreaScaleFallBelowMinSize,
} from "./utils";

const cloneHitAreasSnapshot = (hitAreas = []) =>
  hitAreas.map((hitArea) => ({
    ...hitArea,
    keyframes: (hitArea?.keyframes || []).map((frame) => ({ ...frame })),
  }));

const hasScalableImageHitAreas = (component) =>
  component?.componentType === "Image" &&
  Array.isArray(component?.componentStyles?.hitAreas) &&
  component.componentStyles.hitAreas.length > 0;

/**
 * Scale hit areas when an Image component's width/height changes.
 * Returns synced hitAreas, or null when no sync is needed.
 */
export const scaleHitAreasForImageSizeChange = ({
  component,
  previousWidth,
  previousHeight,
  nextWidth,
  nextHeight,
  setAnimationObject,
} = {}) => {
  if (!hasScalableImageHitAreas(component)) return null;

  const oldWidth = toNumber(previousWidth, 0);
  const oldHeight = toNumber(previousHeight, 0);
  const newWidth = toNumber(nextWidth, 0);
  const newHeight = toNumber(nextHeight, 0);

  // Avoid crushing / exploding hit areas when either size is 0 during
  // live resize or keyframe popup typing.
  if (oldWidth <= 0 || oldHeight <= 0 || newWidth <= 0 || newHeight <= 0) {
    return null;
  }

  if (oldWidth === newWidth && oldHeight === newHeight) return null;

  // Skip sync when shrink would push any hit area below 5×5.
  if (
    wouldHitAreaScaleFallBelowMinSize(
      component.componentStyles.hitAreas,
      oldWidth,
      oldHeight,
      newWidth,
      newHeight
    )
  ) {
    return null;
  }

  const syncedHitAreas = syncHitAreasToParentImageSize(
    component.componentStyles.hitAreas,
    oldWidth,
    oldHeight,
    newWidth,
    newHeight
  );

  if (typeof setAnimationObject === "function") {
    setAnimationObject((previousAnimationObject) =>
      rebuildHitAreaAnimationEntries(previousAnimationObject, syncedHitAreas)
    );
  }

  return syncedHitAreas;
};

/**
 * Panel / timeline convenience: sync from a source Image component onto
 * a next component that already has updated width/height.
 */
export const applyHitAreaScaleFromComponentResize = ({
  sourceComponent,
  nextComponent,
  dimensionName,
  setAnimationObject,
} = {}) => {
  if (dimensionName !== "width" && dimensionName !== "height") return null;

  return scaleHitAreasForImageSizeChange({
    component: sourceComponent,
    previousWidth: sourceComponent?.componentStyles?.width,
    previousHeight: sourceComponent?.componentStyles?.height,
    nextWidth: nextComponent?.componentStyles?.width,
    nextHeight: nextComponent?.componentStyles?.height,
    setAnimationObject,
  });
};

/**
 * Live canvas resize: scale from a gesture baseline so rounding does not drift.
 * Mutates layer.componentStyles.hitAreas when applicable.
 */
export const syncHitAreasDuringCanvasImageResize = (
  baselineRef,
  layer,
  previousWidth,
  previousHeight,
  nextWidth,
  nextHeight
) => {
  if (!baselineRef || !hasScalableImageHitAreas(layer)) return;

  const oldWidth = toNumber(previousWidth, 0);
  const oldHeight = toNumber(previousHeight, 0);
  const newWidth = toNumber(nextWidth, 0);
  const newHeight = toNumber(nextHeight, 0);

  if (oldWidth <= 0 || oldHeight <= 0 || newWidth <= 0 || newHeight <= 0) {
    return;
  }

  if (oldWidth === newWidth && oldHeight === newHeight) return;

  if (!baselineRef.current || baselineRef.current.id !== layer.id) {
    baselineRef.current = {
      id: layer.id,
      width: oldWidth,
      height: oldHeight,
      hitAreas: cloneHitAreasSnapshot(layer.componentStyles.hitAreas),
    };
  }

  const baseline = baselineRef.current;

  // Keep baseline geometry when the live size would crush hit areas below 5×5.
  if (
    wouldHitAreaScaleFallBelowMinSize(
      baseline.hitAreas,
      baseline.width,
      baseline.height,
      newWidth,
      newHeight
    )
  ) {
    layer.componentStyles.hitAreas = cloneHitAreasSnapshot(baseline.hitAreas);
    return;
  }

  layer.componentStyles.hitAreas = syncHitAreasToParentImageSize(
    baseline.hitAreas,
    baseline.width,
    baseline.height,
    newWidth,
    newHeight
  );
};

/**
 * Canvas resize-stop: rebuild hit-area animation entries and clear baseline.
 */
export const finalizeHitAreasAfterCanvasImageResize = (
  baselineRef,
  layerId,
  layerCompData,
  setAnimationObject
) => {
  const baseline = baselineRef?.current;
  if (!baseline || baseline.id !== layerId) {
    if (baselineRef) baselineRef.current = null;
    return;
  }

  if (typeof setAnimationObject === "function") {
    const resizedLayer = (layerCompData || []).find(
      (layer) => layer.id === layerId
    );
    const syncedHitAreas = resizedLayer?.componentStyles?.hitAreas;
    if (Array.isArray(syncedHitAreas) && syncedHitAreas.length > 0) {
      setAnimationObject((previousAnimationObject) =>
        rebuildHitAreaAnimationEntries(previousAnimationObject, syncedHitAreas)
      );
    }
  }

  baselineRef.current = null;
};
